import Component from '@glimmer/component';
import Drawer from 'tio-reimagined/components/drawer';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatTime from 'tio-common/helpers/safe-format-time';

import Textarea from 'tio-reimagined/components/textarea';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import Button from 'tio-reimagined/components/button';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';
import { tracked } from '@glimmer/tracking';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type CommentModel from 'tio-data/models/comment';
import { eq } from 'tio-ui/utilities';

interface PartnerMessagingDrawerArgs {
  isOpen: boolean;
  onClose: () => void;
  application: TasApplicationModel;
}

interface ApproverEmployeeCommentArgs {
  comment: CommentModel;
}

class ApproverEmployeeComment extends Component<ApproverEmployeeCommentArgs> {
  @service declare sessionContext: SessionContextService;
  get initials() {
    const [firstName = '', lastName = ''] = (this.args.comment.commentor ?? '').split(' ');
    return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
  }
  get today() {
    return new Date().toISOString();
  }
  get self() {
    return (
      `${this.sessionContext.user.person.firstName} ${this.sessionContext.user.person.lastName}` ===
      this.args.comment.commentor
    );
  }
  <template>
    <div class="relative flex items-center bg-white px-6 py-5">
      <div class="flex flex-col self-start mr-2">
        <div
          class="flex items-center justify-center text-sm tio-bg-brand h-8 w-8 text-white font-bold rounded-full
            {{if this.self '!bg-ocean-600'}}"
        >
          {{this.initials}}
        </div>
      </div>
      <div class="min-w-0 flex-1">
        <span class="absolute inset-0" aria-hidden="true"></span>
        <p class="text-sm font-medium">{{@comment.commentor}}
          <span class="tio-fg-secondary text-xs font-light">
            {{#if (eq (safeFormatDate this.today) (safeFormatDate @comment.createdAt))}}
              {{safeFormatTime @comment.createdAt}}
            {{else}}
              {{safeFormatDate @comment.createdAt month="long" day="2-digit" year="numeric"}}
            {{/if}}
          </span>
        </p>
        <p class="text-sm tio-fg-muted">{{@comment.comment}}</p>

      </div>
    </div>
  </template>
}

export default class PartnerMessagingDrawer extends Component<PartnerMessagingDrawerArgs> {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @tracked commentToAdd = '';

  addComment = () => {
    const commentable = this.args.application;

    const comment = this.store.createRecord('comment', {
      commentable: commentable,
      commentableType: 'Application',
      createdAt: new Date().toISOString(),
      comment: this.commentToAdd,
      person: this.sessionContext.user.person,
      tags: ['Partner Messaging'],
    }) as CommentModel;

    comment.save().then(() => {
      this.commentToAdd = '';
    });
  };

  updateComment = (value: string) => {
    this.commentToAdd = value;
  };

  get comments() {
    return this.args.application.comments
      ? [...this.args.application.comments]
          .filter((comment) => comment.tags?.includes('Partner Messaging'))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      : [];
  }

  get isDisabled() {
    return !this.commentToAdd.trim();
  }

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <h2 class="tio-fg-secondary font-semibold">
          {{t "tas.partner_messaging_drawer.application_comments"}}
        </h2>
      </:header>

      <:body>
        <div class="overflow-y-auto flex-1">
          {{#each this.comments as |comment|}}
            <ApproverEmployeeComment @comment={{comment}} />
          {{/each}}
        </div>
      </:body>

      <:footer>
        <div class="flex flex-col gap-2 w-full">
          <Textarea
            rows="3"
            placeholder={{t "tas.partner_messaging_drawer.placeholder"}}
            @value={{this.commentToAdd}}
            @onInput={{this.updateComment}}
          />
          <Button class="w-full" {{on "click" this.addComment}} @isDisabled={{this.isDisabled}}>
            {{t "tas.partner_messaging_drawer.add_comment"}}
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>
}
